import { useEffect, useRef } from 'react';
import type { GalleryImage, ModeloCasa, ProjectEntry } from '../content/types';
import { predecode } from '../lib/decode';
import { useDragScroll } from '../lib/useDragScroll';
import { Arrow, ExpandIcon, PinIcon, ProjectLogo, StatusBadge } from '../ui/brand';
import { useContent } from '../ui/ContentContext';
import { SmartImg } from '../ui/SmartImg';
import { SwipeTrack, type SwipeApi } from './SwipeTrack';

export type Tab = 'fotos' | 'renders' | 'casas' | 'mapa';
export const TAB_LABEL: Record<Tab, string> = { fotos: 'Fotos reales', renders: 'Renders', casas: 'Casa modelo', mapa: 'Mapa' };
const TAB_SINGULAR: Record<Tab, string> = { fotos: 'Foto real', renders: 'Render', casas: 'Casa modelo', mapa: 'Ubicación' };

export function tabItems(p: ProjectEntry, t: Tab): GalleryImage[] {
  return t === 'fotos' ? p.fotos : t === 'renders' ? p.renders : t === 'casas' ? p.casas || [] : p.mapa || [];
}

/** Tipos de casa del proyecto. Un manifiesto anterior a los tipos trae los renders sueltos:
 *  se muestran como un único modelo. */
export function modelosDe(p: ProjectEntry): ModeloCasa[] {
  if (p.modelos?.length) return p.modelos;
  return p.casas?.length ? [{ id: `${p.id}:t:0`, nombre: 'Casa modelo', detalle: '', imagenes: p.casas }] : [];
}

/** En "Casa modelo" con varios tipos, primero se elige cuál ver */
export function eligiendoModelo(p: ProjectEntry, tab: Tab, modelo: number | null): boolean {
  return tab === 'casas' && modelosDe(p).length > 1 && modelo == null;
}

/** Las imágenes que se recorren: en "Casa modelo", solo las del tipo abierto */
export function verItems(p: ProjectEntry, tab: Tab, modelo: number | null): GalleryImage[] {
  if (tab !== 'casas') return tabItems(p, tab);
  const ms = modelosDe(p);
  if (ms.length === 1) return ms[0].imagenes;
  return modelo == null ? [] : ms[modelo]?.imagenes || [];
}

/** El tipo abierto (solo si hay más de uno para elegir) */
export function modeloAbierto(p: ProjectEntry, tab: Tab, modelo: number | null): ModeloCasa | null {
  const ms = modelosDe(p);
  return tab === 'casas' && ms.length > 1 && modelo != null ? ms[modelo] || null : null;
}

/** Orden de las pestañas: Fotos reales | Renders | Casa modelo | Mapa */
export function projectTabs(p: ProjectEntry): Tab[] {
  // Solo las pestañas con imágenes cargadas (sin "Próximamente")
  return (['fotos', 'renders', 'casas', 'mapa'] as Tab[]).filter((t) => tabItems(p, t).length > 0);
}

/** Pestaña con la que abre el proyecto: la primera que tenga imágenes */
export function defaultTab(p: ProjectEntry): Tab {
  return projectTabs(p)[0] || 'renders';
}

export function imageLabel(tab: Tab, img: GalleryImage | undefined, i: number) {
  return img?.caption || `${TAB_SINGULAR[tab]} ${i + 1}`;
}

/** 02 · Galería del proyecto */
export function Gallery({
  project,
  tab,
  idx,
  modelo,
  onTab,
  onIdx,
  onModelo,
  onBack,
  onViewer,
}: {
  project: ProjectEntry;
  tab: Tab;
  idx: number;
  /** Casa modelo con varios tipos: el abierto (null = se muestran los tipos) */
  modelo: number | null;
  onTab: (t: Tab) => void;
  onIdx: (i: number) => void;
  onModelo: (m: number | null) => void;
  onBack: () => void;
  onViewer: () => void;
}) {
  const { content } = useContent();
  const src = content.src;
  const items = verItems(project, tab, modelo);
  const eligiendo = eligiendoModelo(project, tab, modelo);
  const abierto = modeloAbierto(project, tab, modelo);
  const n = items.length;
  const i = Math.min(idx, Math.max(0, n - 1));
  const swipe = useRef<SwipeApi | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  useDragScroll(strip, 'x');

  // Vecinas listas antes de que el usuario deslice
  useEffect(() => {
    if (n < 2) return;
    predecode([src(items[(i + 1) % n].main), src(items[(i - 1 + n) % n].main), src(items[(i + 2) % n]?.main)], true);
  }, [i, items, n, src]);

  // La miniatura activa siempre visible
  useEffect(() => {
    const el = strip.current?.children[i] as HTMLElement | undefined;
    const box = strip.current;
    if (!el || !box) return;
    const left = el.offsetLeft - (box.clientWidth - el.offsetWidth) / 2;
    box.scrollTo({ left, behavior: 'smooth' });
  }, [i, tab]);

  return (
    <div className="screen flex flex-col" data-screen="galeria">
      {/* Héroe: imagen principal */}
      <div className="a-drop relative flex-none overflow-hidden bg-placeholder" style={{ height: 1000 }}>
        {eligiendo ? (
          <ModelPickerTotem modelos={modelosDe(project)} onOpen={onModelo} />
        ) : n > 0 ? (
          <SwipeTrack items={items} index={i} onIndex={onIdx} onTap={onViewer} apiRef={swipe} />
        ) : (
          <div className="fill flex flex-col items-center justify-center bg-primario text-center" style={{ gap: 20, padding: '0 120px' }}>
            <div className="rounded bg-secundario" style={{ width: 96, height: 8 }} />
            <div className="font-bold text-blanco" style={{ fontSize: 48 }}>
              Aún no hay imágenes del proyecto
            </div>
          </div>
        )}

        {eligiendo ? null : <div className="pointer-events-none absolute left-0 right-0 top-0" style={{ height: 360, background: 'linear-gradient(180deg, rgba(28,41,76,0.7) 0%, rgba(28,41,76,0) 100%)' }} />}


        {n > 1 ? (
          <>
            <RoundButton side="left" onClick={() => swipe.current?.go(-1)} />
            <RoundButton side="right" onClick={() => swipe.current?.go(1)} />
          </>
        ) : null}

        {n > 0 ? (
          <div className="pointer-events-none absolute flex items-center justify-between" style={{ left: 64, right: 64, bottom: 40, animation: 'tkUp 520ms 360ms var(--ease-out) both' }}>
            <div className="flex items-center rounded-full bg-primario font-semibold text-blanco" style={{ height: 64, padding: '0 28px', fontSize: 26 }}>
              {i + 1} / {n}
            </div>
            <button
              type="button"
              onClick={onViewer}
              className="pressable pointer-events-auto flex items-center rounded-full bg-secundario font-bold text-primario"
              style={{ height: 80, padding: '0 36px', fontSize: 26, gap: 14 }}
            >
              <ExpandIcon size={30} />
              Ampliar
            </button>
          </div>
        ) : null}
      </div>

      {/* Controles */}
      <div className="flex min-h-0 flex-1 flex-col" style={{ paddingTop: 32 }}>
        {/* Ficha fija del proyecto (logo, estado, nombre, ubicación): no cambia al pasar las imágenes */}
        <div className="flex flex-none flex-col" style={{ padding: '0 64px', gap: 10, marginBottom: 28, animation: 'tkUp 520ms 200ms var(--ease-out) both' }}>
          <div className="flex items-center justify-between" style={{ gap: 24 }}>
            {project.logo ? <ProjectLogo p={project} k={0.75} /> : <span />}
            <StatusBadge estado={project.estado} />
          </div>
          <div className="font-bold leading-tight text-primario" style={{ fontSize: 40 }}>
            {project.nombre}
          </div>
          {project.ubicacion ? (
            <div className="flex items-center text-primario/70" style={{ fontSize: 24, gap: 8 }}>
              <PinIcon size={24} />
              {project.ubicacion}
            </div>
          ) : null}
        </div>
        {/* "Regresar" a media altura del tótem (~1 m del suelo): alcanzable sin agacharse */}
        <div className="flex flex-none" style={{ padding: '0 64px', marginBottom: 32, animation: 'tkLeft 520ms 240ms var(--ease-out) both' }}>
          <button
            type="button"
            onClick={onBack}
            className="pressable flex items-center rounded-full bg-primario font-bold text-blanco"
            style={{ height: 96, padding: '0 44px 0 12px', fontSize: 30, gap: 20, boxShadow: '0 10px 28px rgba(28,41,76,0.28)' }}
          >
            <span className="flex flex-none items-center justify-center rounded-full bg-secundario text-primario" style={{ width: 72, height: 72 }}>
              <Arrow dir="left" size={36} />
            </span>
            Regresar
          </button>
        </div>
        {/* El selector solo aparece si hay más de una pestaña con imágenes */}
        <div className="flex-none" style={{ padding: '0 64px', animation: 'tkUp 520ms 300ms var(--ease-out) both', display: projectTabs(project).length > 1 ? undefined : 'none' }}>
          <div className="flex rounded-full bg-primario" style={{ height: 96, padding: 8 }}>
            {projectTabs(project).map((t, k) => (
              <div key={t} className="flex flex-1" style={{ marginLeft: k ? 8 : 0 }}>
                <Segment active={tab === t} disabled={tabItems(project, t).length === 0} onClick={() => onTab(t)} label={TAB_LABEL[t]} />
              </div>
            ))}
          </div>
        </div>

        {eligiendo ? (
          <div className="flex flex-none items-center text-primario/70" style={{ padding: '0 64px', marginTop: 40, gap: 14, fontSize: 26, animation: 'tkUp 420ms 200ms var(--ease-out) both' }}>
            <span className="flex-none rounded-full bg-secundario" style={{ width: 14, height: 14 }} />
            Toca un modelo para ver sus renders
          </div>
        ) : null}

        {/* Tipo de casa abierto: su nombre y la vuelta a los demás tipos */}
        {abierto ? (
          <div className="flex flex-none items-center" style={{ padding: '0 64px', marginTop: 40, gap: 24, animation: 'tkUp 420ms var(--ease-out) both' }}>
            <button
              type="button"
              onClick={() => onModelo(null)}
              className="pressable flex flex-none items-center rounded-full bg-primario font-bold text-blanco"
              style={{ height: 80, padding: '0 36px 0 10px', fontSize: 26, gap: 16 }}
            >
              <span className="flex flex-none items-center justify-center rounded-full bg-secundario text-primario" style={{ width: 60, height: 60 }}>
                <Arrow dir="left" size={30} />
              </span>
              Otros modelos
            </button>
            <div className="min-w-0">
              <div className="truncate font-bold leading-tight text-primario" style={{ fontSize: 32 }}>{abierto.nombre}</div>
              {abierto.detalle ? <div className="truncate text-primario/70" style={{ fontSize: 22 }}>{abierto.detalle}</div> : null}
            </div>
          </div>
        ) : null}

        {n > 0 ? (
          <>
            <div ref={strip} className="noscroll flex flex-none overflow-x-auto" style={{ padding: '6px 64px', marginTop: abierto ? 32 : 48, touchAction: 'pan-x' }}>
              {items.map((t, k) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onIdx(k)}
                  className="relative flex-none overflow-hidden"
                  style={{ width: 300, height: 300, borderRadius: 32, marginRight: k < n - 1 ? 24 : 0, animation: `tkUp 480ms ${360 + Math.min(k, 6) * 60}ms var(--ease-out) both` }}
                >
                  <SmartImg file={t.thumb} lqip={t.lqip} className="fill" />
                  <div className="pointer-events-none fill" style={{ borderRadius: 32, border: `6px solid ${k === i ? '#1C294C' : 'transparent'}`, transition: 'border-color 160ms' }} />
                </button>
              ))}
            </div>
          </>
        ) : null}

        <div className="flex-1" />

      </div>
    </div>
  );
}

// Sin el número de imágenes de cada pestaña (Kevin, 2026-10-29): solo el nombre
/** Los tipos de casa en tarjetas sobre el héroe del tótem: el cliente ve cuántos modelos
 *  puede construir y el asesor toca el que quiera mostrar. */
function ModelPickerTotem({ modelos, onOpen }: { modelos: ModeloCasa[]; onOpen: (k: number) => void }) {
  const una = modelos.length <= 2;
  return (
    <div className="fill noscroll flex flex-col overflow-y-auto bg-primario" style={{ padding: '56px 64px 48px', touchAction: 'pan-y' }}>
      <div className="flex flex-none items-center text-blanco" style={{ gap: 18, marginBottom: 32, animation: 'tkUp 420ms var(--ease-out) both' }}>
        <span className="flex-none rounded-full bg-secundario" style={{ width: 64, height: 10 }} />
        <span className="font-bold" style={{ fontSize: 36 }}>{modelos.length} modelos de casa para construir</span>
      </div>
      <div className="grid flex-1" style={{ gridTemplateColumns: una ? '1fr' : '1fr 1fr', gridAutoRows: una ? 'minmax(0, 1fr)' : 380, gap: 28 }}>
        {modelos.map((m, k) => (
          <button
            key={m.id}
            type="button"
            onClick={() => onOpen(k)}
            className="pressable relative overflow-hidden text-left"
            style={{ borderRadius: 36, background: '#27345A', animation: `tkUp 480ms ${120 + Math.min(k, 8) * 70}ms var(--ease-out) both` }}
          >
            <SmartImg file={m.imagenes[0]?.main} lqip={m.imagenes[0]?.lqip} className="fill" />
            <span className="pointer-events-none absolute inset-x-0 bottom-0" style={{ height: '65%', background: 'linear-gradient(0deg, rgba(28,41,76,0.94), rgba(28,41,76,0))' }} />
            <span className="absolute inset-x-0 bottom-0 flex items-end justify-between" style={{ padding: 28, gap: 20 }}>
              <span className="min-w-0">
                <span className="block truncate font-bold leading-tight text-blanco" style={{ fontSize: 34 }}>{m.nombre}</span>
                {m.detalle ? <span className="block truncate text-blanco/80" style={{ fontSize: 22, marginTop: 4 }}>{m.detalle}</span> : null}
              </span>
              <span className="flex flex-none items-center justify-center rounded-full bg-secundario text-primario" style={{ width: 72, height: 72 }}>
                <Arrow dir="right" size={34} />
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Segment({ active, disabled, onClick, label }: { active: boolean; disabled?: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={disabled ? undefined : onClick}
      className="flex flex-1 items-center justify-center rounded-full font-bold"
      style={{
        gap: 12,
        fontSize: 26,
        background: active ? '#C6D300' : 'transparent',
        color: active ? '#1C294C' : disabled ? 'rgba(255,255,255,0.4)' : '#FFFFFF',
        transition: 'background-color 200ms, color 200ms',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <span>{label}</span>
      {disabled ? (
        // Lima como texto: permitido aquí porque el fondo es azul oscuro
        <span className="rounded-full border-2 border-secundario/70 font-semibold text-secundario" style={{ fontSize: 16, padding: '3px 12px' }}>
          Próximamente
        </span>
      ) : null}
    </button>
  );
}

function RoundButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const right = side === 'right';
  return (
    <button
      type="button"
      aria-label={right ? 'Siguiente' : 'Anterior'}
      onClick={onClick}
      className={`pressable absolute flex items-center justify-center rounded-full text-primario ${right ? 'bg-secundario' : 'bg-blanco'}`}
      style={{ top: 500 - 48, [side]: 32, width: 96, height: 96, boxShadow: '0 6px 20px rgba(14,20,38,0.25)' }}
    >
      <Arrow dir={side} size={40} />
    </button>
  );
}
